import { API_URLS } from '@helpers/api';
import { getSvgIcon, Icon } from '@helpers/icons';
import { clamp } from '@helpers/utils';
import { withMotionMax } from '@hoc/withMotionMax';
import { useApi } from '@hooks/useApi';
import { useForm, validateNotWhitespaceOnly } from '@hooks/useForm';
import { useOutsideClick } from '@hooks/useOutsideClick';
import Button from '@stories/Components/Buttons/Button/Button';
import { Theme } from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import { AnimatePresence } from 'framer-motion';
import qs from 'qs';
import * as React from 'react';
import { useFirstMountState } from 'react-use';
import S from './SearchBar.styles';
const SearchBar = (props) => {
    const isFirstMount = useFirstMountState();
    const { onSubmit, useReactHookFormReturn: { watch, register, setValue, setFocus, formState: { errors }, }, status, } = useForm({
        endpoint: props.submitUrl ? props.submitUrl : API_URLS.SEARCH,
        mode: props.submitUrl ? 'querystring' : 'json',
    });
    const { searchTerm: searchValue } = watch();
    const [searchTerm, setSearchTerm] = React.useState(props.searchTerm ?? '');
    const [showSuggestions, setShowSuggestions] = React.useState(false);
    const [selectedTerm, setSelectedTerm] = React.useState('');
    // Suggestions aren't needed for MVP
    React.useEffect(() => {
        if (searchValue &&
            searchValue.length > 1 &&
            searchTerm !== searchValue &&
            searchValue !== selectedTerm) {
            setSearchTerm(searchValue);
            // setShowSuggestions(true);
        }
        // else {
        //   setShowSuggestions(false);
        // }
    }, [searchValue]);
    // Submit the search term when the user selects a suggestion
    React.useEffect(() => {
        if (!isFirstMount && selectedTerm === searchValue) {
            props.onSubmit?.(selectedTerm);
        }
    }, [selectedTerm, searchValue]);
    React.useEffect(() => {
        setSearchTerm(props.searchTerm ?? '');
        setValue('searchTerm', props.searchTerm ?? '');
    }, [props.searchTerm]);
    const result = useApi({
        url: `${API_URLS.SEARCH_TYPEAHEAD}?${qs.stringify({
            searchTerm,
        })}`,
        validate: () => !!props.enableSuggestions &&
            !!searchTerm &&
            searchTerm.length > 2 &&
            searchTerm !== props.searchTerm,
    });
    const itemsRef = React.useRef([]);
    const containerRef = useOutsideClick(() => setShowSuggestions(false));
    return (React.createElement(Wrapper, { ...props },
        React.createElement(S.FormWrapper, { ref: containerRef },
            React.createElement(S.Form, { onSubmit: props.onSubmit !== undefined
                    ? (event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        props.onSubmit?.(searchTerm);
                        showSuggestions && setShowSuggestions(false);
                    }
                    : onSubmit, "data-sr-only": props.isComponent },
                React.createElement(S.Label, { id: `${props.idPrefix ?? ''}-searchbox-label`, htmlFor: `${props.idPrefix ?? ''}-search` }, props.label),
                React.createElement(S.InputWrapper, { "data-error": !!errors.searchTerm },
                    React.createElement(S.Input, { id: `${props.idPrefix ?? ''}-search`, type: "search", placeholder: props.searchPlaceholder ?? props.translations?.['global.search.placeholder'], role: "combobox", "aria-autocomplete": "list", "aria-expanded": showSuggestions && !!result.data && result.data.suggestions.length > 0, "aria-controls": `${props.idPrefix ?? ''}-typeahead-listbox`, "aria-labelledby": `${props.idPrefix ?? ''}-searchbox-label`, ...register('searchTerm', {
                            required: props.translations?.['global.search.validation'] ?? '',
                            validate: (value) => validateNotWhitespaceOnly(value, props.translations?.['global.search.validation'] ?? ''),
                        }), "aria-invalid": !!errors.searchTerm, "aria-errormessage": `${props.idPrefix ?? ''}-searchbox-error`, onKeyDown: (event) => {
                            switch (event.key) {
                                case 'ArrowDown':
                                    if (itemsRef.current.length > 0) {
                                        itemsRef.current[0]?.focus();
                                    }
                                    break;
                                case 'Escape':
                                    setShowSuggestions(false);
                                    break;
                            }
                        }, "aria-disabled": status === 'pending', autoComplete: "off" }),
                    React.createElement(AnimatePresence, null, searchValue !== '' ? (React.createElement(S.Button, { layoutId: `${props.idPrefix ?? ''}-search-clear-button`, type: "button", "aria-label": props.translations?.['global.search.clear'], "data-type": "clear", initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: 0 }, onClick: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setValue('searchTerm', '');
                            setSearchTerm('');
                            props.onSubmit?.('');
                        } }, getSvgIcon(Icon.CloseCircle))) : null),
                    props.textButton ? (React.createElement(Button, { type: "submit", buttonType: "solid" }, props.translations?.['global.search.submit'])) : (React.createElement(S.Button, { type: "submit", "aria-label": props.translations?.['global.search.submit'], "aria-disabled": status === 'pending' }, getSvgIcon(Icon.Search))))),
            React.createElement(AnimatePresence, null, showSuggestions && result.data && result.data.suggestions.length > 0 ? (React.createElement(S.Suggestions, { id: `${props.idPrefix ?? ''}-typeahead-listbox`, "aria-hidden": !result.data || result.data.suggestions.length === 0, tabIndex: showSuggestions ? -1 : undefined, initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: 0 } }, result.data.suggestions.map((suggestion, i) => {
                const regex = new RegExp(searchTerm, 'gi');
                const textValue = suggestion.value.replaceAll(regex, (match) => `<span>${match}</span>`);
                return (React.createElement("li", { key: suggestion.id }, suggestion.url !== undefined ? (React.createElement(S.AutosuggestOption, { ref: (el) => (itemsRef.current[i] = el), as: "a", href: suggestion.url, onKeyDown: (e) => onKeyPress(e, i, (result.data?.suggestions).length - 1), tabIndex: showSuggestions ? -1 : undefined, dangerouslySetInnerHTML: { __html: textValue } })) : (React.createElement(S.AutosuggestOption, { ref: (el) => (itemsRef.current[i] = el), onClick: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setValue('searchTerm', suggestion.value);
                        setSelectedTerm(suggestion.value);
                        props.onSubmit?.(suggestion.value);
                    }, onKeyDown: (e) => onKeyPress(e, i, (result.data?.suggestions).length - 1), tabIndex: showSuggestions ? -1 : undefined, dangerouslySetInnerHTML: { __html: textValue } }))));
            }))) : null))));
    function onKeyPress(event, i, maxIndex) {
        const nextIndex = i === maxIndex ? 0 : clamp(i + 1, maxIndex);
        const prevIndex = i === 0 ? maxIndex : clamp(i - 1, maxIndex);
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                event.stopPropagation();
                itemsRef.current[nextIndex]?.focus();
                break;
            case 'ArrowUp':
                event.preventDefault();
                event.stopPropagation();
                itemsRef.current[prevIndex]?.focus();
                break;
            case 'Escape':
                setShowSuggestions(false);
                setFocus('searchTerm');
                break;
            default:
                break;
        }
    }
};
const Wrapper = React.forwardRef((props, ref) => {
    if (!props.children) {
        return null;
    }
    if (props.isComponent) {
        return React.createElement(React.Fragment, null, props.children);
    }
    return (React.createElement(S.Container, { ref: ref, id: props.anchorId ?? '', background: Theme.Grey }, props.children));
});
Wrapper.displayName = 'SearchBar';
export default withMotionMax(SearchBar);
